import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Container,
  Typography,
  Button,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  Switch,
  IconButton,
  Box,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Add, Edit, Search } from "@mui/icons-material"; // Added Search icon
import { Navigate, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { blockUser, editUser, getUserByType } from "../redux/slices/user/thunk";
import LoadingComponent from "../components/LoadingComponent";

const FamilyManagementPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const familiyUsers = useSelector((state) => state.usersSlice.usersByType);
  const usersLoading = useSelector((state) => state.usersSlice.usersLoading);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(""); // State for search term
  const user = JSON.parse(localStorage.getItem('user'))

  useEffect(() => {
    dispatch(getUserByType({ type: "family" }));
  }, [dispatch]);

  const handleAddFamily = () => {
    navigate("/addUser");
  };

  const handleEdit = (id) => {
    navigate(`/editUser/${id}`);
  };

  // Filter families based on search term
  const filteredFamilies = useMemo(() => {
    if (!searchTerm.trim()) return familiyUsers;
    
    const term = searchTerm.toLowerCase().trim();
    return familiyUsers.filter(family => {
      const nameMatch = family.userName?.toLowerCase().includes(term) || false;
      const emailMatch = family.email?.toLowerCase().includes(term) || false;
      
      // Combine phone fields for matching
      const phoneFields = [
        family.phoneNumber || "",
        family.countryCode?.replace("+", "") || ""
      ].join(" ");
      
      const phoneMatch = phoneFields.toLowerCase().includes(term);
      
      return nameMatch || emailMatch || phoneMatch;
    });
  }, [familiyUsers, searchTerm]);

      if (user?.type !== "admin") {
    return <Navigate to="/" replace />;
  }

  if (usersLoading) {
    return <LoadingComponent />;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">{t("familyManagement.title")}</Typography>
        <Button
          variant="contained"
          startIcon={<Add sx={{ ml: 1 }} />}
          onClick={handleAddFamily}
        >
          {t("familyManagement.addFamily")}
        </Button>
      </Box>

      {/* Search Bar */}
      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={t("familyManagement.searchPlaceholder")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            sx: {
              borderRadius: 1,
              height: 50,
              backgroundColor: "background.paper",
            }
          }}
        />
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">{t("familyManagement.table.image")}</TableCell>
              <TableCell align="center">{t("familyManagement.table.name")}</TableCell>
              <TableCell align="center">{t("familyManagement.table.phone")}</TableCell>
              <TableCell align="center">{t("familyManagement.table.email")}</TableCell>
              <TableCell align="center">{t("familyManagement.table.status")}</TableCell>
              <TableCell align="center">{t("familyManagement.table.edit")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredFamilies?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1">
                    {searchTerm
                      ? t("familyManagement.noResults")
                      : t("familyManagement.noFamilies")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredFamilies.map((fam) => (
                <TableRow key={fam._id}>
                  {/* Image */}
                  <TableCell align="center">
                    <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                      {fam.image ? (
                        <Avatar src={fam.image} alt={fam.userName} />
                      ) : (
                        <Avatar>{fam.userName?.charAt(0).toUpperCase()}</Avatar>
                      )}
                    </Box>
                  </TableCell>

                  {/* Name */}
                  <TableCell align="center">{fam.userName}</TableCell>

                  {/* Phone */}
                  <TableCell align="center">
                    {fam.countryCode?.replace("+", "")}
                    {fam.phoneNumber}
                  </TableCell>

                  {/* Email */}
                  <TableCell align="center">{fam.email}</TableCell>

                  {/* Status */}
                  <TableCell align="center">
                    <Switch
                      checked={fam.status}
                      disabled={loading}
                      color="primary"
                      onClick={async () => {
                        const id = fam?._id;
                        const data = { status: !fam?.status };
                        try {
                          setLoading(true);
                          await dispatch(blockUser({ id: fam._id, data: { status: !fam.status } }));
                          await dispatch(getUserByType({ type: "family" }));
                          setLoading(false);
                        } catch (error) {
                          console.log("error", error);
                          setLoading(false);
                        }
                      }}
                    />
                  </TableCell>

                  {/* Edit */}
                  <TableCell align="center">
                    <IconButton color="primary" onClick={() => handleEdit(fam._id)}>
                      <Edit />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default FamilyManagementPage;